<template>
    <pc-loader v-bind:loading="loading">
        <pc-table
            v-bind:items="historyItems"
            v-bind:columns="columns"
            v-bind:initial-sort="[[0, 'desc']]"
            key-prop="AttachmentId"
            hide-search>
            <template v-slot:FileName="item">
                <file-name-column
                    is-admin-page
                    hide-view-button
                    v-bind:wide-view="wideView"
                    v-bind:attachment="item"
                    v-on:remove-attachment="onRemoveAttachmentClick(item)"
                    v-on:download-attachment="downloadAttachment(item)" />
            </template>
            <template v-slot:Status="item">
                <span v-bind:class="getApprovalStatusDisplayClass(item.Status, true)">
                    {{ getApprovalStatusDisplay(item.Status, true) }}
                </span>
            </template>
            <template v-slot:Note="item">
                <attachment-note v-bind:note="item.Note" />
            </template>
            <template v-slot:AiAssistantName="item">
                {{ item.AiAssistantName ? item.AiAssistantName : "" }}
            </template>
            <template
                v-if="!wideView"
                v-slot:actionsRow="item">
                <pc-button v-on:click="downloadAttachment(item)">
                    {{ downloadButtonLabel }}
                </pc-button>
            </template>
        </pc-table>
    </pc-loader>
</template>
<script>
    import { getApprovalStatusDisplay, getApprovalStatusDisplayClass } from "Util/clientAttachment";
    import { getClientTimestampTableColumn } from "Util/clientDateTime";
    import { formatBasicUserInfoForDisplay } from "Util/clientUser";
    import { isAtLeast } from "Responsive/manager";
    import { WIDE_BP } from "Projects/SharedCapsules/_sharedCapsuleBase";

    import attachmentNote from "./attachmentNote.vue";
    import fileNameColumn from "./fileNameColumn.vue";

    import { KEYS, NAMESPACE } from "./i18n/attachmentsCapsule.i18n";
    import { KEYS as UTIL_KEYS, NAMESPACE as UTIL_NAMESPACE } from "./i18n/attachmentsUtil.i18n";
    import { mapI18n } from "Util/i18n";

    export default {
        components: {
            attachmentNote,
            fileNameColumn
        },
        inject: {
            viewport: {
                from: "viewport",
                default: null
            }
        },
        props: {
            attachment: { type: Object, required: true },
            history: { type: Array, required: true }
        },
        emits: ["download-attachment", "remove-attachment"],
        data() {
            return {
                historyItems: this.history.map((h) => {
                    const copy = JSON.parse(JSON.stringify(h));
                    copy.showFullNote = false;
                    return copy;
                }),

                showConfirm: false,
                loading: false,
                attachmentToRemove: null
            };
        },
        computed: {
            ...mapI18n(KEYS, NAMESPACE),
            ...mapI18n(
                {
                    ...UTIL_KEYS,
                    confirmRemovalMessage() {
                        return {
                            key: UTIL_KEYS.confirmRemovalMessageLabel,
                            values: {
                                fileName: this.attachmentToRemove.FileName
                            }
                        };
                    }
                },
                UTIL_NAMESPACE
            ),
            columns() {
                const cols = [
                    getClientTimestampTableColumn(this.uploadTimestampHeader, "UploadTimestamp"),
                    {
                        header: this.filenameHeader,
                        property: "FileName",
                        isCardHeader: true,
                        columnClass: "attachment-history-filename-cell-wrapper"
                    },
                    {
                        header: this.noteHeader,
                        property: "Note",
                        columnClass: "attachment-history-note-cell-wrapper"
                    },
                    {
                        header: this.statusHeader,
                        property: "Status",
                        sortValue: (item) => getApprovalStatusDisplay(item.Status, true)
                    },
                    {
                        header: this.statusChangeUserHeader,
                        property: "StatusChangeUser",
                        displayValue: (user) => formatBasicUserInfoForDisplay(user, null, true),
                        sortValue: (item) => formatBasicUserInfoForDisplay(item.StatusChangeUser, null, true)
                    },
                    getClientTimestampTableColumn(this.statusChangeTimestampHeader, "StatusChangeTimestamp")
                ];

                if (this.$app.LoggedInRole.RolePrivileges.ProjectAdminCanUseAIAttachmentAssistants) {
                    cols.push({
                        header: this.aiAssistantNameHeader,
                        property: "AiAssistantName",
                        displayValue: (assistantName) => assistantName || "",
                        sortValue: (item) => item.AiAssistantName || ""
                    });
                    cols.push(
                        getClientTimestampTableColumn(
                            this.aiAssistantRunTimestampHeader,
                            "AiAssistantRunTimestamp",
                            "",
                            false,
                            false
                        )
                    );
                }

                return cols.map((c) => {
                    c.orderable = true;
                    c.visible = true;
                    return c;
                });
            },
            wideView() {
                return isAtLeast(this.viewport, WIDE_BP);
            }
        },
        methods: {
            downloadAttachment(attachment) {
                this.$emit("download-attachment", attachment);
            },
            getApprovalStatusDisplay,
            getApprovalStatusDisplayClass
        }
    };
</script>

<style lang="scss" scoped>
    :deep(.attachment-history-filename-cell-wrapper) {
        min-width: 150px;
    }

    :deep(.attachment-history-note-cell-wrapper) {
        min-width: 185px;
    }
</style>
